import { Tooltip } from "@/components/tooltip";
import { TogglThemeProvider } from "@hundred5/design-system";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import App, { AppContext, AppProps } from "next/app";
import { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import { ErrorHandler } from "../components/error-handler";
import { FlashMessagesProvider } from "../components/flash-messages";
import { GlobalStyles } from "../styles/global-styles";
import { useConsoleMessage } from "../utils";
import { BrowserFingerprintProvider } from "../utils/browser-fingerprint";

import { HttpClientProvider } from "@/components/http-client-provider/http-client-provider";
import { GoogleReCaptcha } from "@/utils/google-recaptcha";
import "@fortawesome/fontawesome-svg-core/styles.css";
import "../locales/setup";
import "../styles/fonts.css";

const NON_RETRIABLE_ERRORS = [404, 401, 500];

export default function MyApp({ Component, pageProps }: AppProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
            refetchOnMount: false,
            retry: (failureCount, error) => {
              // @ts-ignore-next-line FIXME: the actual error object is different than the typing
              if (NON_RETRIABLE_ERRORS.includes(error?.response?.status)) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
      }),
  );

  useEffect(() => {
    if (ref.current != null) {
      ReactModal.setAppElement(ref.current);
    }
  }, []);

  useConsoleMessage();

  return (
    <Sentry.ErrorBoundary fallback={<ErrorHandler />}>
      <TogglThemeProvider>
        <I18nProvider i18n={i18n}>
          <FlashMessagesProvider>
            <HttpClientProvider>
              <QueryClientProvider client={queryClient}>
                <GlobalStyles />
                <BrowserFingerprintProvider>
                  <div id="app" ref={ref}>
                    <Component {...pageProps} />
                  </div>
                </BrowserFingerprintProvider>
                <Tooltip />
                <GoogleReCaptcha />
                <ReactQueryDevtools />
              </QueryClientProvider>
            </HttpClientProvider>
          </FlashMessagesProvider>
        </I18nProvider>
      </TogglThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};
